'use client';

import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { forwardRef, type ReactNode } from 'react';
import type { BaseInputProps } from 'src/app/_components/base/input';
import BaseInput from 'src/app/_components/base/input';
import KottonseedLabel from 'src/app/(default)/_components/_common/Label';
import { cn } from 'src/app/lib/utils/tailwindcss';

type InputProps = BaseInputProps &
  VariantProps<typeof inputVariants> & {
    /** 인풋 라벨 */
    label?: string;
    /** 오류 메시지 */
    errorMessage?: string;
    /** 성공 여부 */
    success?: boolean;
    /** 보조 텍스트 (좌측) */
    helperText?: string;
    /** 보조 텍스트 (우측) */
    rightHelperText?: string;
    /** 인풋 앞에 표시될 요소 */
    startAdornment?: ReactNode;
    /** 인풋 뒤에 표시될 요소 */
    endAdornment?: ReactNode;
    /** 인풋 바깥쪽에 표시될 요소 */
    action?: ReactNode;
  };

const inputVariants = cva('', {
  variants: {
    variant: {
      default: 'typo-body-3',
      round: 'typo-body-6',
    },
  },
});

const inputBoxVariants = cva('', {
  variants: {
    variant: {
      default: 'rounded-lg',
      round: 'rounded-3xl',
    },
  },
});

const KottonseedInput = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant = 'default',
      label,
      errorMessage,
      success,
      helperText,
      rightHelperText,
      startAdornment,
      endAdornment,
      action,
      className,
      ...props
    }: InputProps,
    ref,
  ) => {
    return (
      <div className="flex flex-col gap-2">
        {label && <KottonseedLabel htmlFor={props.id}>{label}</KottonseedLabel>}

        {/* 인풋 + 버튼 한 줄 구성 */}
        <div className="flex items-center gap-2">
          {/* 인풋 박스 */}
          <div
            className={cn(
              inputBoxVariants({ variant }),
              'grid h-11 flex-1 items-center overflow-hidden bg-g-20',
              'border border-transparent focus-within:border-k-50',
              errorMessage && 'border border-rd-50 focus-within:border-rd-50',
              success && 'border border-gn-50 focus-within:border-gn-50',
              startAdornment && endAdornment
                ? 'grid-cols-[auto_1fr_auto]'
                : startAdornment
                  ? 'grid-cols-[auto_1fr]'
                  : endAdornment
                    ? 'grid-cols-[1fr_auto]'
                    : 'grid-cols-[1fr]',
            )}
          >
            {startAdornment && <div className="flex items-center pl-5">{startAdornment}</div>}

            <BaseInput
              className={cn(
                inputVariants({ variant }),
                'h-full w-full bg-transparent px-5 text-g-90 transition-all outline-none',
                'disabled:cursor-not-allowed disabled:rounded-lg disabled:border-g-30 disabled:bg-g-30 disabled:text-g-60',
                'placeholder:text-g-40',
                startAdornment && 'pl-2',
                endAdornment && 'pr-2',
                className,
              )}
              ref={ref}
              {...props}
            />

            {endAdornment && <div className="flex items-center pr-5">{endAdornment}</div>}
          </div>

          {/* 인풋 바깥쪽 버튼 (옵션) */}
          {action}
        </div>

        {/* 보조 텍스트 영역 (좌측 및 우측) */}
        {(errorMessage || helperText || rightHelperText) && (
          <div className="flex justify-between">
            <div>
              {errorMessage && <p className="text-xs leading-3.5 whitespace-pre-line text-rd-50">{errorMessage}</p>}
              {!errorMessage && helperText && (
                <p className={cn('text-xs leading-3.5 whitespace-pre-line', success ? 'text-gn-50' : 'text-g-40')}>
                  {helperText}
                </p>
              )}
            </div>
            {!errorMessage && rightHelperText && (
              <p className={cn('text-xs leading-3.5 whitespace-pre-line', success ? 'text-gn-50' : 'text-g-40')}>
                {rightHelperText}
              </p>
            )}
          </div>
        )}
      </div>
    );
  },
);

KottonseedInput.displayName = 'KottonseedInput';

export default KottonseedInput;
